/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, May 2020
 */
import { Nullable } from '../../../core/lib';
import {
    SLASH_PROTOCOLS,
    PROTOCOL_CHARACTERS,
    HOST_TERMINAL_CHARACTERS,
    NO_PREPEND_SLASH_HOST_TERMINAL_CHARACTERS,
    ESCAPE_CHARACTERS,
    CHAR_AT,
    CHAR_SPACE,
    CHAR_SLASH,
    CHAR_QUESTION_MARK,
    CHAR_HASHTAG,
    CHAR_BACKSLASH,
    CHAR_COLON,
    CHAR_LOWER_CASE_A,
    CHAR_PERIOD,
    CHAR_UPPER_CASE_A,
    CHAR_UPPER_CASE_Z,
    CHAR_MINUS,
    CHAR_UNDERSCORE,
    CHAR_PLUS,
    CHAR_ZERO,
    CHAR_NINE,
    CHAR_PERCENT,
    CHAR_OPENING_BRACKET,
    CHAR_OPENING_BRACE
} from './constants';
import { URLSearch } from './URLSearch';
import { searchCharacterCode, removeEndingSlash } from './utils';

/**
 * A blazing fast URL parser with the same semantics (mostly, we're missing IDNA support) as the legacy Node.js URL parser.
 * For more information see: https://nodejs.org/docs/latest-v10.x/api/url.html#url_legacy_url_api.
 */
export class URL {
    /**
     * The URL protocol.
     */
    public protocol: string;
    /**
     * The host of the URL (i.e. no port or protocol).
     */
    public hostname: string;
    /**
     * True of two ASCII forward slashes are required after the protocol.
     */
    public slashes: boolean;
    /**
     * URL authentication.
     */
    public auth: string;
    /**
     * The port number of the URL.
     */
    public port: number;
    /**
     * The contents of the URL path (without the query string).
     */
    public pathname: string;
    /**
     * The query string of the URL.
     */
    public search: URLSearch;
    /**
     * The contents of the URL hash.
     */
    public hash: string;

    private queryString?: string;
    private prependSlash = false;

    /**
     * Create a URL object from a string.
     *
     * @param url The URL to parse.
     */
    constructor(url?: string) {
        if (url) {
            this.parse(url);
        }
    }

    /**
     * Check if a URL is absolute (i.e. it has a host) or relative.
     *
     * @returns True if the URL is in relative form.
     */
    public isAbsolute(): boolean {
        return Boolean(this.hostname);
    }

    /**
     * Append a URL to the current one.
     *
     * @param url URL to append.
     */
    public join(url: URL | string): URL {
        if (typeof url === 'string') url = new URL(url);

        const composed = new URL();
        composed.protocol = this.protocol ?? url.protocol;
        composed.hostname = this.hostname ?? url.hostname;
        composed.port = isNaN(this.port) ? url.port : this.port;
        composed.search = new URLSearch();

        // Remove the ending slash of the instance's pathname
        // since we're sure that the pathname of the `url` begins with a slash.
        composed.pathname = this.pathname
            ? removeEndingSlash(this.pathname) + url.pathname
            : url.pathname;

        if (this.search) {
            composed.search.merge(this.search);
        }
        if (url.search) {
            composed.search.merge(url.search);
        }

        composed.hash = this.hash ? `${this.hash}${url.hash}` : url.hash;

        return composed;
    }

    /**
     * Convert the URL to a string.
     *
     * @returns String the converted URL encoded.
     */
    public toString(): string {
        let auth = this.auth || '';

        if (auth) {
            auth = encodeURIComponent(auth);
            auth = auth.replace(/%3A/i, ':');
            auth += '@';
        }

        let protocol = this.protocol ?? '';
        let pathname = this.pathname ?? '';
        let hash = this.hash ?? '';
        let search = this.queryString ?? '';
        let hostname = this.hostname ?? '';
        let host: Nullable<string> = null;
        let scheme = '';

        const port = Number.isNaN(this.port) ? '' : this.port;

        if (!search) {
            search = this.search.isEmpty() ? '' : `?${this.search}`;
        }

        if (protocol && protocol.charCodeAt(protocol.length - 1) !== CHAR_COLON /* ':' */)
            protocol += ':';

        if (this.hostname) {
            const ip6 = hostname.includes(':');
            if (ip6) hostname = `[${hostname}]`;
            host = auth + hostname + (port ? `:${port}` : '');
        } else {
            host = auth;
        }

        const slashes =
            this.slashes || ((!protocol || SLASH_PROTOCOLS.has(protocol)) && host !== null);

        if (protocol) scheme = protocol + (slashes ? '//' : '');
        else if (slashes) scheme = '//';

        if (slashes && pathname && pathname.charCodeAt(0) !== CHAR_SLASH /* '/' */) {
            pathname = `/${pathname}`;
        }
        if (search && search.charCodeAt(0) !== CHAR_QUESTION_MARK /* '?' */) search = `?${search}`;
        if (hash && hash.charCodeAt(0) !== CHAR_HASHTAG /* '#' */) hash = `#${hash}`;

        search = this.escapeSearch(search);
        pathname = this.escapePath(pathname);

        return scheme + (host === null ? '' : host) + pathname + search + hash;
    }

    private parse(url: string): void {
        let start = 0;
        let end = url.length - 1;

        // Trim leading and trailing ws
        while (url.charCodeAt(start) <= CHAR_SPACE) start += 1;
        while (url.charCodeAt(end) <= CHAR_SPACE) end -= 1;

        start = this.parseProtocol(url, start, end);
        start = this.parseHost(url, start, end);
        const proto = this.protocol;
        if (!this.hostname && (this.slashes || (proto && !SLASH_PROTOCOLS.has(proto)))) {
            this.hostname = '';
        }

        if (start <= end) {
            const code = url.charCodeAt(start);

            if (code === CHAR_SLASH || code === CHAR_SLASH) {
                this.parsePath(url, start, end);
            } else if (code === CHAR_QUESTION_MARK) {
                this.parseQuery(url, start, end);
            } else if (code === CHAR_HASHTAG) {
                this.parseHash(url, start, end);
            } else {
                this.parsePath(url, start, end);
            }
        }

        // Adding default values.

        // All protocols in SLASH_PROTOCOLS need a path.
        if (!this.pathname && this.hostname && SLASH_PROTOCOLS.has(this.protocol)) {
            this.pathname = '/';
        }
        // Clean the hash value.
        if (!this.hash) {
            this.hash = '';
        }
        if (!this.auth) {
            this.auth = '';
        }

        let { queryString: search } = this;
        if (search == null) {
            search = '';
            this.queryString = search;
        }
        if (search.charCodeAt(0) === CHAR_QUESTION_MARK) {
            search = search.slice(1);
        }

        this.port = (this.port ?? -1) >= 0 ? this.port : NaN;
        this.search = new URLSearch(search);
        this.protocol = this.protocol ? `${this.protocol}:` : this.protocol;
    }

    private parseHost(url: string, start: number, end: number): number {
        const first = url.charCodeAt(start);
        const second = url.charCodeAt(start + 1);

        if (
            (first === CHAR_SLASH /* '/' */ || first === CHAR_BACKSLASH) /* '\' */ &&
            (second === CHAR_SLASH /* '/' */ || second === CHAR_BACKSLASH) /* '\' */
        ) {
            this.slashes = true;

            // The string starts with //
            if (start === 0) {
                // The string is just "//"
                if (end < 2) return start;
                // If slashes do not denote host and there is no auth,
                // there is no host when the string starts with //
                const withAuth = searchCharacterCode(url, CHAR_AT, 2, HOST_TERMINAL_CHARACTERS);
                if (!withAuth) {
                    this.slashes = false;
                    return start;
                }
            }
            // There is a host that starts after the //
            start += 2;
        }
        // If there is no slashes, there is no hostname if
        // 1. there was no protocol at all
        else if (
            !this.protocol ||
            // 2. there was a protocol that requires slashes
            // e.g. in 'http:asd' 'asd' is not a hostname
            SLASH_PROTOCOLS.has(this.protocol)
        ) {
            return start;
        }

        let lowerCase = false;
        let hostNameStart = start;
        let hostNameEnd = end;
        let portLength = 0;
        let charsAfterDot = 0;
        let authNeedsDecoding = false;

        let j = -1;

        // Find the last occurrence of an @-sign until hostending character is met
        // also mark if decoding is needed for the auth portion
        for (let i = start; i <= end; i += 1) {
            const ch = url.charCodeAt(i);

            if (ch === CHAR_AT /* '@' */) {
                j = i;
            }
            // This check is very, very cheap.
            // An unnecessary decodeURIComponent is very, very inefficient.
            else if (ch === CHAR_PERCENT /* '%' */) {
                authNeedsDecoding = true;
            } else if (HOST_TERMINAL_CHARACTERS[ch] === 1) {
                break;
            }
        }

        // @-sign was found at index j, everything to the left from it
        // is auth part
        if (j > -1) {
            this.parseAuth(url, start, j - 1, authNeedsDecoding);
            // hostname starts after the last @-sign
            hostNameStart = j + 1;
            start = hostNameStart;
        }

        // Host name is starting with a [
        if (url.charCodeAt(start) === CHAR_OPENING_BRACKET /* '[' */) {
            for (let i = start + 1; i <= end; i += 1) {
                const ch = url.charCodeAt(i);

                // Assume valid IP6 is between the brackets
                if (ch === CHAR_OPENING_BRACKET /* ']' */) {
                    if (url.charCodeAt(i + 1) === CHAR_COLON /* ':' */) {
                        portLength = this.parsePort(url, i + 2, end) + 1;
                    }
                    const hostname = url.slice(start + 1, i).toLowerCase();
                    this.hostname = hostname;
                    this.pathname = '/';
                    return i + portLength + 1;
                }
            }
            // Empty hostname, [ starts a path
            return start;
        }

        for (let i = start; i <= end; i += 1) {
            if (charsAfterDot > 62) {
                this.hostname = url.slice(start, i);
                return i;
            }
            const code = url.charCodeAt(i);

            if (code === CHAR_COLON) {
                portLength = this.parsePort(url, i + 1, end) + 1;
                hostNameEnd = i - 1;
                break;
            } else if (code < CHAR_LOWER_CASE_A) {
                if (code === CHAR_PERIOD) {
                    charsAfterDot = -1;
                } else if (code >= CHAR_UPPER_CASE_A && code <= CHAR_UPPER_CASE_Z) {
                    lowerCase = true;
                }
                // Valid characters other than ASCII letters -, _, +, 0-9
                else if (
                    !(
                        code === CHAR_MINUS ||
                        code === CHAR_UNDERSCORE ||
                        code === CHAR_PLUS ||
                        (code >= CHAR_ZERO && code <= CHAR_NINE)
                    )
                ) {
                    if (
                        HOST_TERMINAL_CHARACTERS[code] === 0 &&
                        NO_PREPEND_SLASH_HOST_TERMINAL_CHARACTERS[code] === 0
                    ) {
                        this.prependSlash = true;
                    }
                    hostNameEnd = i - 1;
                    break;
                }
            } else if (code >= CHAR_OPENING_BRACE /* '{' */) {
                throw new Error(`Intenationalized domain names are not supported at the moment`);
            }
            charsAfterDot += 1;
        }

        if (hostNameEnd + 1 !== start && hostNameEnd - hostNameStart <= 256) {
            let hostname = url.slice(hostNameStart, hostNameEnd + 1);
            if (lowerCase) hostname = hostname.toLowerCase();
            this.hostname = hostname;
        }

        return hostNameEnd + 1 + portLength;
    }

    private parseProtocol(url: string, start: number, end: number): number {
        let lowerCase = false;

        for (let i = start; i <= end; i += 1) {
            const charCode = url.charCodeAt(i);

            if (charCode === CHAR_COLON /* ':' */) {
                let protocol = url.slice(start, i);
                if (lowerCase) protocol = protocol.toLowerCase();
                this.protocol = protocol;
                return i + 1;
            }
            if (PROTOCOL_CHARACTERS[charCode] === 1) {
                if (charCode < CHAR_LOWER_CASE_A) lowerCase = true;
            } else {
                return start;
            }
        }
        return start;
    }

    private parsePath(url: string, start: number, end: number): void {
        const pathStart = start;
        let pathEnd = end;
        const pathPrefix = this.port === -2 ? '/:' : '';

        for (let i = start; i <= end; i += 1) {
            const char = url.charCodeAt(i);
            if (char === CHAR_HASHTAG /* '#' */) {
                this.parseHash(url, i, end);
                pathEnd = i - 1;
                break;
            } else if (char === CHAR_QUESTION_MARK /* '?' */) {
                this.parseQuery(url, i, end);
                pathEnd = i - 1;
                break;
            }
        }

        if (pathStart > pathEnd) {
            this.pathname = pathPrefix === '' ? '/' : pathPrefix;
            return;
        }

        const path = this.escape(url, pathStart, pathEnd);

        if (pathPrefix === '') {
            this.pathname = this.prependSlash ? `/${path}` : path;
        } else {
            this.pathname = pathPrefix + path;
        }
    }

    private parseQuery(url: string, start: number, end: number): void {
        const queryStart = start;
        let queryEnd = end;

        for (let i = start; i <= end; i += 1) {
            const char = url.charCodeAt(i);

            if (char === CHAR_HASHTAG /* '#' */) {
                this.parseHash(url, i, end);
                queryEnd = i - 1;
                break;
            }
        }

        if (queryStart > queryEnd) {
            this.queryString = '';
            return;
        }

        const query = this.escape(url, queryStart, queryEnd);

        this.queryString = query;
    }

    private parseHash(str: string, start: number, end: number): void {
        if (start > end) {
            this.hash = '';
            return;
        }

        this.hash = this.escape(str, start, end);
    }

    private parseAuth(url: string, start: number, end: number, decode: boolean): void {
        let auth = url.slice(start, end + 1);
        if (decode) {
            auth = decodeURIComponent(auth);
        }
        this.auth = auth;
    }

    private parsePort(str: string, start: number, end: number): number {
        // Internal format is integer for more efficient parsing
        // and for efficient trimming of leading zeros
        let port = 0;
        // Distinguish between :0 and : (no port number at all)
        let hadChars = false;
        let validPort = true;
        let i = 0;

        for (i = start; i <= end; i += 1) {
            const char = str.charCodeAt(i);

            if (char /* '0' */ >= CHAR_ZERO && char <= CHAR_NINE /* '9' */) {
                port = 10 * port + (char - CHAR_ZERO) /* '0' */;
                hadChars = true;
            } else {
                validPort = false;
                if (char === CHAR_BACKSLASH /* '\' */ || char === CHAR_SLASH /* '/' */) {
                    validPort = true;
                }
                break;
            }
        }
        if ((port === 0 && !hadChars) || !validPort) {
            if (!validPort) {
                this.port = -2;
            }
            return 0;
        }

        this.port = port;
        return i - start;
    }

    private escape(string: string, start: number, end: number): string {
        let current = start;
        let i = start;
        let escaped = '';

        for (; i <= end; i += 1) {
            const char = string.charCodeAt(i);
            const escapedCode = ESCAPE_CHARACTERS[char];

            if (escapedCode) {
                if (current < i) escaped += string.slice(current, i);
                escaped += escapedCode;
                current = i + 1;
            }
        }
        if (current < i + 1) escaped += string.slice(current, i);
        return escaped;
    }

    private escapePath(pathname: string): string {
        return pathname.replace(/[?#]/g, match => encodeURIComponent(match));
    }

    private escapeSearch(search: string): string {
        return search.replace(/#/g, match => encodeURIComponent(match));
    }
}



// WEBPACK FOOTER //
// ./src/packages/http/lib/URL/URL.ts